import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";

const CHANGE = { added: "bg-emerald-50 text-emerald-700", removed: "bg-rose-50 text-rose-700", changed: "bg-amber-50 text-amber-700", product_swap: "bg-blue-50 text-blue-700" };
const fmt = (v) => (v && typeof v === "object" ? (v.product_short || v.product_code || JSON.stringify(v)) : String(v ?? "—"));

/** Per-version summaries plus the item-level log: what, previous → new, who, when, source, CO. */
export default function FrsRevisions({ project, overview, tick }) {
  const [revs, setRevs] = useState(null);
  const [filter, setFilter] = useState({ version_id: "", item_key: "" });

  useEffect(() => {
    setRevs(null);
    opsApi.frsRevisions(project.id, { ...filter, limit: 500 }).then((r) => setRevs(r.revisions)).catch(() => toast.error("Could not load revisions"));
  }, [project.id, filter, tick]);

  return (
    <div className="space-y-4" data-testid="ops-frs-revisions">
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Versions</div>
        {overview.versions.map((v) => (
          <button key={v.id} onClick={() => setFilter({ version_id: filter.version_id === v.id ? "" : v.id, item_key: "" })}
                  className={`flex w-full items-start gap-3 border-b border-slate-50 px-3 py-2 text-left text-sm hover:bg-slate-50 ${filter.version_id === v.id ? "bg-slate-50" : ""}`}
                  data-testid={`ops-frs-version-row-${v.version_no}`}>
            <span className={`mt-0.5 rounded px-1.5 py-0.5 text-[11px] font-semibold ${v.kind === "original" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>v{v.version_no}</span>
            <div className="min-w-0 flex-1">
              <div className="text-slate-800">{v.summary || v.notes || `${v.kind} · ${v.source}`}</div>
              <div className="text-xs text-slate-500">{v.kind} · {v.source}{v.file_name ? ` · ${v.file_name}` : ""} · {v.item_count} items / {v.unit_total} units · {v.created_by} · {(v.created_at || "").replace("T", " ").slice(0, 16)}</div>
            </div>
          </button>
        ))}
        {!overview.versions.length && <div className="px-3 py-4 text-sm text-slate-500">No versions yet.</div>}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center gap-3 border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Item changes {filter.version_id && <span className="font-normal normal-case text-slate-400">(this version only)</span>}
          {filter.item_key && <button onClick={() => setFilter({ ...filter, item_key: "" })} className="font-normal normal-case text-blue-600">clear item filter</button>}
        </div>
        {!revs ? <div className="flex items-center gap-2 px-3 py-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div> : (
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500"><tr>
              <th className="px-3 py-2">When</th><th className="px-3 py-2">Room</th><th className="px-3 py-2">Item</th><th className="px-3 py-2">Change</th><th className="px-3 py-2">Previous → New</th><th className="px-3 py-2">Who</th><th className="px-3 py-2">Source / reason</th>
            </tr></thead>
            <tbody>
              {revs.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`ops-frs-rev-${r.id}`}>
                  <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">{(r.at || "").replace("T", " ").slice(0, 16)}</td>
                  <td className="px-3 py-1.5 text-slate-600">{r.room}</td>
                  <td className="px-3 py-1.5"><button onClick={() => setFilter({ ...filter, item_key: r.frs_item_key })} className="text-left text-slate-800 hover:underline" title="Show this item's history">{r.product_short || r.scope_code}</button></td>
                  <td className="px-3 py-1.5"><span className={`rounded px-1.5 py-0.5 ${CHANGE[r.change] || ""}`}>{r.change === "product_swap" ? "product swap" : `${r.change}${r.field && r.change === "changed" ? ` · ${r.field}` : ""}`}</span></td>
                  <td className="px-3 py-1.5 tabular-nums"><span className="line-through text-slate-400">{fmt(r.previous)}</span> → <span className="font-medium">{fmt(r.new)}</span></td>
                  <td className="px-3 py-1.5 text-slate-600">{r.actor}</td>
                  <td className="px-3 py-1.5 text-slate-600">{r.source}{r.reason ? ` · ${r.reason}` : ""}{r.related_co_id ? ` · CO ${r.related_co_id}` : ""}{r.related_submittal_id ? ` · submittal ${r.related_submittal_id}` : ""}</td>
                </tr>
              ))}
              {!revs.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">No item changes recorded.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
